import { writeFileSync, mkdirSync, readFileSync, existsSync } from "node:fs";
import { resolve, dirname } from "node:path";
import type { WorkflowState } from "./workflow-state.js";

interface RegistryFeature {
  id: string;
  name: string;
  estimated_tokens: number;
  status: string;
}

interface BuildPromptOptions {
  projectName: string;
  workflowState: WorkflowState;
  registryPath?: string;
}

/**
 * Generate a build prompt at .fathom/build-prompt.md.
 * Contains project progress, priority queue, and per-feature details.
 * Regenerated fresh each time — not persisted across sessions.
 */
export function generateBuildPrompt(opts: BuildPromptOptions): string {
  const { projectName, workflowState } = opts;
  const regPath = opts.registryPath ?? resolve(process.cwd(), ".fathom", "registry.json");

  // Load registry for feature details
  let features: RegistryFeature[] = [];
  if (existsSync(regPath)) {
    try {
      const data = JSON.parse(readFileSync(regPath, "utf-8"));
      if (Array.isArray(data.features)) features = data.features;
    } catch {
      // continue without features
    }
  }

  // Load spec content if available
  let specContent = "";
  if (workflowState.specPath && existsSync(workflowState.specPath)) {
    specContent = readFileSync(workflowState.specPath, "utf-8");
  }

  // Build the priority queue from workflow state
  const queueFeatures = workflowState.buildQueue
    .map((id) => features.find((f) => f.id === id))
    .filter((f): f is RegistryFeature => f !== undefined);

  const pct = workflowState.featuresTotal > 0
    ? Math.round((workflowState.featuresComplete / workflowState.featuresTotal) * 100)
    : 0;

  const lines: string[] = [
    `# Build Context — ${projectName}`,
    "",
    "## Progress",
    "",
    `- **Features:** ${workflowState.featuresComplete}/${workflowState.featuresTotal} complete (${pct}%)`,
    `- **Budget:** $${workflowState.totalSpent.toFixed(2)} of $${workflowState.totalBudget.toFixed(2)} spent`,
    "",
    "## Priority Queue",
    "",
    "Work on these features in order:",
    "",
  ];

  if (queueFeatures.length > 0) {
    queueFeatures.forEach((f, i) => {
      lines.push(`${i + 1}. **${f.name}** (\`${f.id}\`) — est. ${f.estimated_tokens.toLocaleString()} tokens`);
    });
  } else {
    lines.push("_(no features in queue)_");
  }

  lines.push("");

  // Add spec content for reference
  if (specContent) {
    lines.push(
      "## Spec Reference",
      "",
      "The full spec is included below. Each feature has its own section with description, tasks, and acceptance criteria.",
      "",
      "---",
      "",
      specContent,
    );
  }

  lines.push(
    "",
    "## Instructions",
    "",
    `- Reference feature IDs (e.g., \`[${queueFeatures[0]?.id ?? "feature-001"}]\`) in your work so sessions auto-tag`,
    "- Work through the priority queue in order",
    "- Use branch conventions: `feat/<feature-id>-<short-name>`",
    `- Registry location: \`.fathom/registry.json\``,
    "",
  );

  const content = lines.join("\n");

  // Write to .fathom/build-prompt.md
  const promptPath = resolve(process.cwd(), ".fathom", "build-prompt.md");
  mkdirSync(dirname(promptPath), { recursive: true });
  writeFileSync(promptPath, content);

  return promptPath;
}
